// CARO. Casa de estilo — shared domain types.
// These types are the contract between the Worker (BFF) and the React client.

export type Locale = 'es' | 'en' | 'fr';

export type ProductCategory = 'beauty' | 'casa' | 'estilo';

/** Every product category (store), in a stable order. */
export const PRODUCT_CATEGORIES: readonly ProductCategory[] = ['beauty', 'casa', 'estilo'];

export type ProductStatus =
  | 'draft'
  | 'active'
  | 'sold_out'
  | 'on_hold'
  | 'archived';

/** direct = normal add-to-cart checkout | enquiry = request info (no checkout) */
export type CheckoutMode = 'direct' | 'enquiry';

export interface ProductImage {
  url: string;
  alt: string;
}

/**
 * Public product shape returned by the API.
 * Money is always integer cents; the browser never computes authoritative price.
 */
export interface Product {
  id: string;
  slug: string;
  sku: string | null;
  brand: string | null;
  name: string;
  subtitle: string | null;
  description: string | null;
  /** CARO's editorial recommendation — the "Por qué lo elige CARO" note. */
  caroNote: string | null;
  category: ProductCategory;
  subcategory: string | null;
  productType: string | null;
  priceCents: number;
  compareAtPriceCents: number | null;
  currency: string;
  vatRate: number; // e.g. 0.21 for 21% — see src/lib/tax
  stockQuantity: number;
  inventoryTracking: boolean;
  status: ProductStatus;
  checkoutMode: CheckoutMode;
  featured: boolean;
  sortOrder: number;
  image: string | null;
  gallery: ProductImage[];
  /** Verified-data flag: false = placeholder awaiting real manufacturer data. */
  dataVerified: boolean;
  metadata: ProductMetadata;
  createdAt: string;
  updatedAt: string;
}

/** Structured, optional editorial/spec fields (JSON column in D1). */
export interface ProductMetadata {
  benefits?: string[];
  howToUse?: string;
  ingredients?: string;
  inci?: string;
  size?: string;
  warnings?: string;
  ean?: string;
  edition?: string; // e.g. "Unique piece", "Limited edition"
  faq?: { q: string; a: string }[];
  shippingNote?: string;
  /**
   * Reasortimiento (pre-order / back-order). When true the product is sold even
   * with no stock on hand: the server-authoritative cart bypasses the
   * out-of-stock / insufficient-stock blocks and the store keeps it visible, so
   * a piece still on its way to CARO can be bought now. The storefront shows a
   * delivery-delay notice (see `preorderNote`). Independent of `inventoryTracking`;
   * `decrementStock` already clamps at 0, so tracked pre-orders never go negative.
   */
  preorder?: boolean;
  /**
   * Optional per-product override for the delivery-delay notice shown on
   * pre-order products. When empty the storefront falls back to the default
   * bilingual notice (`pdp.preorderNotice`). Localizable via `i18n.preorderNote`.
   */
  preorderNote?: string;
  /**
   * One-off piece (e.g. a NAOR teddy): once sold out it shows «Pieza vendida»
   * instead of a restock alert, since it will not come back (see
   * `isSoldPiece` / `acceptsRestockAlert` in `@/lib/catalog/availability`).
   * Editable in /admin («Única»). Independent of the free-text `edition`.
   */
  uniquePiece?: boolean;
  /**
   * «Hallazgos» (finds: Estilo + `uniquePiece`, see `@/lib/catalog/finds`):
   * where and why Carolina chose the piece. Free text, shown as «La historia»
   * on the find's page. Localizable via `i18n.findStory`.
   */
  findStory?: string;
  /** A find's measurements, free text («Largo 68 cm · Pecho 52 cm»). Localizable via `i18n.findMeasurements`. */
  findMeasurements?: string;
  /** A find's condition (label per locale: dictionary `finds.condition.<value>`; JSON-LD itemCondition). */
  findCondition?: FindCondition;
  /**
   * Scheduled publication (any product): ISO 8601 WITH offset
   * («2026-11-05T19:00:00+01:00»). Until that instant the piece is not live
   * (`isLive` in `@/lib/catalog/availability`): not listed, its page and API
   * answer 404, out of the sitemap / llms.txt and the cart refuses it. Absent =
   * live (no change for existing products).
   */
  publishAt?: string;
  /**
   * Per-UNIT base delivery for SPAIN (SDP) in integer cents, for pieces with
   * their own carrier cost (e.g. fragile design objects). The order's tier
   * multiplier is applied on top: España ×1, Europa continental ×1.5,
   * Internacional ×2.4 (see src/lib/shipping DELIVERY_TIER_MULTIPLIER).
   * When set (>0) this REPLACES the standard zone rate for that item.
   */
  deliverySpainCents?: number;
  /**
   * Optional per-zone overrides for the per-unit delivery (integer cents). When
   * present they REPLACE the tier multiplier for that zone: `deliveryEuropeCents`
   * for the Europa continental zone and `deliveryInternationalCents` for the
   * Internacional catch-all. When absent, the multiplier still applies to
   * `deliverySpainCents` (España ×1, Europa ×1.5, Internacional ×2.4). Use these
   * when a piece's real carrier price per zone doesn't follow the default ratios
   * (e.g. NAOR teddys: 50 € / 70 € / 150 €).
   */
  deliveryEuropeCents?: number;
  deliveryInternationalCents?: number;
  /**
   * Show the enquiry (request-information) form ON a direct-checkout product,
   * BELOW the buy controls — so a collectible piece can be bought outright and
   * still offer a "prefer to ask first?" path. Ignored for `enquiry` products
   * (which already render the form instead of checkout).
   */
  allowEnquiry?: boolean;
  /**
   * Size variants (e.g. `["S","M"]`, in display order). When set, the buyer MUST
   * pick one of them (cart line `size`). NOT `size`, which is the spec text
   * ("50 ml"). Read through `productSizes()` in `@/lib/cart`.
   */
  sizes?: string[];
  /**
   * Units on hand per size (`{ S: 2, M: 1 }`). `stockQuantity` stays the product
   * TOTAL (sum of these); when present, a sized line is also capped by its own
   * size's units. Absent → only the product total applies.
   */
  sizeStock?: Record<string, number>;
  /**
   * Shipping dimensions & weight of ONE unit, for carrier labels (DHL Express /
   * MyDHL) and future rate quotes. `weightGrams` is the packed weight; the
   * dimensions are the outer box in centimetres. Stored in metadata JSON so no
   * DB migration is needed. Used to prefill the DHL shipment fields; DHL bills
   * the greater of actual and volumetric weight (L×W×H/5000 kg for Express).
   */
  weightGrams?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  /**
   * Made-to-measure wooden crate: packed weight and box are decided per shipment
   * at DHL registration (volumetric weight), not fixed in the catalog. Set true
   * for fragile art/design pieces (e.g. Hamed Ouattara). When true the DHL block
   * shows "caja a medida · peso volumétrico en el registro DHL" instead of asking
   * to fill a catalog weight. Standard-package items (e.g. MIIM) leave this unset
   * and fill weightGrams + dimensions instead.
   */
  customCrate?: boolean;
  /**
   * Short SEO title of the product page (`<title>` without the « · CARO. Casa
   * de estilo» suffix), e.g. «Sérum vitamina C 10 % Glow Drops · Meisani». When
   * empty the title is `name · brand` (see productSeoTitle in src/lib/seo). The
   * h1 and og:title stay the product name. Localizable via `i18n.seoTitle`.
   */
  seoTitle?: string;
  /**
   * Per-locale translations of the product's textual fields. Base content is
   * Spanish (es); any field missing here falls back to it. Stored inside the
   * metadata JSON so no DB migration is needed. Applied client-side via
   * localizeProduct() in src/lib/i18n-content.
   */
  i18n?: Record<string, ProductTranslation>;
  [key: string]: unknown;
}

/** Translatable textual fields of a product (all optional → es fallback). */
/** A find's condition (`metadata.findCondition`). */
export type FindCondition = 'new_with_tags' | 'new' | 'excellent' | 'good';

export interface ProductTranslation {
  name?: string;
  subtitle?: string;
  description?: string;
  caroNote?: string;
  size?: string;
  edition?: string;
  shippingNote?: string;
  benefits?: string[];
  howToUse?: string;
  ingredients?: string;
  inci?: string;
  warnings?: string;
  preorderNote?: string;
  seoTitle?: string;
  findStory?: string;
  findMeasurements?: string;
}

// --- Packs -----------------------------------------------------------------
// A pack = N products with quantities and a % discount (docs/plan-packs.md).
// Its price is never stored: see packPricing in src/lib/catalog/packs.ts.

export type PackStatus = 'draft' | 'active' | 'archived';

/** Translatable fields of a pack (`packs.i18n`, per locale; base content is es). */
export interface PackTranslation {
  name?: string;
  description?: string;
}

export interface PackItem {
  productId: string;
  quantity: number;
}

export interface Pack {
  id: string;
  slug: string;
  /** null → default name per locale (packDefaultName). */
  name: string | null;
  description: string | null;
  /** 1..90 */
  discountPercent: number;
  status: PackStatus;
  /** Own 4:5 photo; null → collage of the components' covers. */
  image: string | null;
  /** Store with most units (fixed at creation): default name + `seq`. */
  primaryCategory: ProductCategory;
  /** Sequence number within `primaryCategory` («n1»), fixed at creation. */
  seq: number;
  sortOrder: number;
  i18n: Record<string, PackTranslation>;
  /** Components in display order (`pack_items.position`). */
  items: PackItem[];
  createdAt: string;
  updatedAt: string;
}

/** Public pack shape (API): the pack plus its loaded components and computed price. */
export interface PublicPack extends Pack {
  components: Product[];
  /** Pack price (server-computed, x,99). */
  priceCents: number;
  /** Sum of the components' prices («antes»). */
  compareAtCents: number;
  available: boolean;
  /** Distinct stores of the components, in item order. */
  categories: ProductCategory[];
}

// --- Cart & checkout -------------------------------------------------------

export interface CartLineInput {
  productId: string;
  quantity: number;
  /** Chosen size; required iff the product has `metadata.sizes`. */
  size?: string;
}

/**
 * A pack line as the browser submits it (docs/plan-packs.md): the server
 * expands it into its components (quantity × packs each). `sizes` = chosen size
 * per sized component (`productId → size`), one size for all its units.
 */
export interface PackLineInput {
  packId: string;
  quantity: number;
  sizes?: Record<string, string>;
}

/** A cart line as submitted to quote / checkout: a product or a pack (`isPackLine`, lib/cart). */
export type CartRequestLine = CartLineInput | PackLineInput;

export interface CartLine {
  product: Product;
  quantity: number;
  lineSubtotalCents: number; // unit price * qty (VAT-inclusive in EU retail)
}

export interface OrderTotals {
  subtotalCents: number;
  shippingCents: number;
  /**
   * Part of `shippingCents` that is the temporary fuel surcharge
   * (FUEL_SURCHARGE_PERCENT), for the checkout breakdown. Absent when 0.
   * Not persisted on orders.
   */
  shippingSurchargeCents?: number;
  taxCents: number;
  totalCents: number;
  currency: string;
}

// --- Orders ----------------------------------------------------------------

export type OrderStatus = 'pending' | 'confirmed' | 'cancelled' | 'fulfilled';

export type PaymentStatus =
  | 'pending'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'partially_refunded';

export interface OrderCustomer {
  /** Required — confirmation email + manual resolution. */
  email: string;
  /** Optional — extra delivery contact. */
  phone?: string;
  firstName: string;
  lastName: string;
  billingCountry: string;
  shippingCountry: string;
  shippingAddress1: string;
  shippingAddress2?: string;
  shippingCity: string;
  shippingPostalCode: string;
  shippingProvince?: string;
}

/** A normalized address candidate returned by the validator (for correction). */
export interface SuggestedAddress {
  address1: string;
  city: string;
  postalCode: string;
  province: string;
  country: string; // ISO-3166-1 alpha-2
  label: string; // human-readable full address
}

export interface OrderItemSnapshot {
  productId: string;
  sku: string | null;
  productName: string;
  /** Chosen size (`order_items.size`); null for unsized products and old rows. */
  size: string | null;
  quantity: number;
  unitPriceCents: number;
  vatRate: number;
  taxCents: number;
  lineTotalCents: number;
  /** Pack this line was expanded from (`order_items.pack_id`); null/absent = single line. */
  packId?: string | null;
  /** Pack name at order time (`order_items.pack_name`), immutable copy. */
  packName?: string | null;
}

export interface Order {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  fulfilmentStatus: string;
  customer: OrderCustomer;
  items: OrderItemSnapshot[];
  totals: OrderTotals;
  paymentProvider: string;
  sumupCheckoutId: string | null;
  sumupTransactionId: string | null;
  /** Cart that created the order — used to release its stock holds on paid. */
  cartId: string | null;
  /** Storefront language the buyer ordered in (`orders.locale`, default `es`):
   *  picks the language of the customer confirmation email. */
  locale: Locale;
  createdAt: string;
  updatedAt: string;
}

/** Public, non-sensitive order status (looked up by opaque reference). */
export interface OrderStatusView {
  orderNumber: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  totals: OrderTotals;
  createdAt: string;
}

// --- Editorial & community -------------------------------------------------

export interface Testimonial {
  id: string;
  quote: string;
  firstName: string;
  lastName: string | null;
  location: string | null;
  serviceType: string | null;
  portrait: string | null;
  verified: boolean;
  sortOrder: number;
  active: boolean;
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  /**
   * Publication instant, ISO 8601 with an explicit offset (e.g.
   * `2026-10-20T08:00:00+02:00`). Absent → `date` at 00:00 Madrid. Before it, the
   * article does not exist publicly (API/page 404, not in the sitemap).
   */
  publishAt?: string;
  heroImage: string | null;
  /**
   * Share card (Open Graph) 1200×630 JPEG built from the hero and written here
   * by `npm run og:articles` (`/media/caro/og/edit-<slug>-<hero hash>.jpg`).
   * Absent → the default card.
   */
  ogImage?: string;
  body: string;
  seoTitle: string | null;
  seoDescription: string | null;
  relatedProductSlugs: string[];
  /**
   * Extra closing blocks: `colorimetry` = the COLORIMETRY ME! card (as on /caro/servicios) after the
   * linked products; `silhouette` / `face` = the card of the silhouette or face test, the same kind of
   * block; `services` = a button to the image-consulting services next to the store button; `guide` = a button
   * to the Estudio's guide «Combinar colores», next to them.
   */
  endLinks?: ArticleEndLink[];
  /** Per-locale translations; base is Spanish (es), missing fields fall back. */
  i18n?: Record<string, ArticleTranslation>;
}

export type ArticleEndLink = 'colorimetry' | 'silhouette' | 'face' | 'services' | 'guide';

/** Translatable textual fields of an article (all optional → es fallback). */
export interface ArticleTranslation {
  title?: string;
  excerpt?: string;
  body?: string;
  seoTitle?: string;
  seoDescription?: string;
}

export interface InstagramPost {
  id: string;
  permalink: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM' | 'REEL';
  thumbnailUrl: string;
  caption: string;
  timestamp: string;
  isReel: boolean;
}

// --- Admin (read-only views) ----------------------------------------------

export interface AdminSummary {
  activeProducts: number;
  outOfStock: number;
  unverified: number;
  ordersTotal: number;
  ordersPaid: number;
  revenueCents: number;
}

export interface AdminOrderRow {
  orderNumber: string;
  createdAt: string;
  paymentStatus: PaymentStatus;
  status: OrderStatus;
  totalCents: number;
  currency: string;
  shippingCountry: string;
  email: string | null;
  phone: string | null;
  /** Hidden from the default /admin list (orders.archived_at set). */
  archived: boolean;
}

/** An Edit article as /admin sees it: every article, with its publication state. */
export interface AdminArticle extends Article {
  status: 'published' | 'scheduled';
  /** Effective publication instant (ISO, UTC): `publishAt` or `date` at 00:00 Madrid. */
  publishesAt: string;
}

export interface AdminStockRow {
  slug: string;
  name: string;
  category: ProductCategory;
  status: ProductStatus;
  stock: number;
  /**
   * Sized products (metadata.sizes): units per size in size order, e.g.
   * [{size:'S',units:0},{size:'M',units:2}]. `units` is null when the product
   * has sizes but no sizeStock map (only the total is tracked). Absent = unsized.
   */
  sizes?: { size: string; units: number | null }[];
  inventoryTracking: boolean;
  priceCents: number;
  checkoutMode: CheckoutMode;
  dataVerified: boolean;
}

/**
 * Restock alerts of one product, as /admin → Stock shows them
 * (docs/plan-agotado-aviso.md, fase 4). Counts only: no addresses.
 */
export interface AdminRestockAlertRow {
  slug: string;
  /** Spanish name, or the slug when the product no longer exists. */
  name: string;
  /** The product exists, is `active` and not sold out: «Enviar aviso» may run. */
  available: boolean;
  /** Confirmed, not cancelled, still to notify (includes `failed` retries and stale sends). */
  waiting: number;
  /** Of `waiting`: the last attempt failed or was skipped (it is retried). */
  failed: number;
  /** In flight right now (a send started less than 15 min ago). */
  sending: number;
  /** Requested but never confirmed from the inbox (never notified). */
  unconfirmed: number;
  /** Already notified. */
  sent: number;
}

/** Result of one «Enviar aviso» call (at most RESTOCK_SEND_BATCH emails). */
export interface AdminRestockSendResult {
  sent: number;
  failed: number;
  /** Not sent on purpose: mock gateway without a test inbox, or email not configured (dev). */
  skipped: number;
  /** Confirmed alerts still waiting after this call (press again to continue). */
  remaining: number;
}

/** One drop piece in /admin → Hallazgos → «Carta del drop». */
export interface AdminDropLetterPiece {
  slug: string;
  name: string;
  status: ProductStatus;
  /** Public now (its publishAt has passed). */
  live: boolean;
  /** The letter may offer it now: active, live and its unit still there. */
  available: boolean;
}

/**
 * The «carta del drop» of one drop (docs/plan-hallazgos-drops.md, fase 5), as
 * /admin → Hallazgos shows it. Counts only: no addresses.
 */
export interface AdminDropLetterRow {
  /** The drop instant, ISO UTC (the key of the send endpoint). */
  dropAt: string;
  /** now >= the drop instant. */
  live: boolean;
  /** Why «Enviar» is off (not_live · too_old · sold_out), or null when it may run. */
  blocked: 'not_live' | 'too_old' | 'sold_out' | null;
  pieces: AdminDropLetterPiece[];
  letter: {
    /** Letters already sent for this drop (sends log). */
    sent: number;
    /** Queued and still to send (includes failed / skipped retries and stale sends). */
    waiting: number;
    /** Of `waiting`: the last attempt failed. */
    failed: number;
    /** Of `waiting`: the last attempt was skipped (dev / email not configured). */
    skipped: number;
    /** In flight right now (a send started less than 15 min ago). */
    sending: number;
  };
  /** Subscribers (confirmed, not unsubscribed) who have not received it yet: the N of the button. */
  eligible: number;
  /** The email as it would go out now (null when no piece is available). */
  preview: {
    subjects: Record<Locale, string>;
    /** The Spanish body (with a sample unsubscribe link). */
    textEs: string;
  } | null;
}

/** Result of one «Enviar carta del drop» call (at most 40 emails). */
export type AdminDropLetterSendResult = AdminRestockSendResult;

export type OutboxKind = 'order_confirmation' | 'shipping' | 'review_request';
export type OutboxStatus = 'draft' | 'sending' | 'sent' | 'failed' | 'skipped';

/** A stored customer email (outbox_emails), as /admin → Emails and Postventa show it. */
export interface AdminOrderEmail {
  /** null for a confirmation rebuilt from the order (sent before the emails were stored). */
  id: string | null;
  kind: OutboxKind;
  status: OutboxStatus;
  orderNumber: string | null;
  enquiryId: string | null;
  recipientName: string | null;
  /** Short line for the inbox table. */
  title: string | null;
  /** Who prepared it: the site itself, /admin or the CLI (Tuesday task). */
  source: 'system' | 'admin' | 'cli' | null;
  locale: Locale;
  from: string;
  to: string;
  bcc: string[];
  subject: string;
  text: string;
  /** The branded HTML part, exactly as the customer gets it. */
  html: string;
  carrier: string | null;
  carrierName: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  detail: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  sentAt: string | null;
  archivedAt: string | null;
  /** Rebuilt from the order data: the original text was not stored. */
  reconstructed?: boolean;
}

/** Same record in the inbox. */
export type AdminOutboxEmail = AdminOrderEmail;

/** One campaign in «Enviados» (read only, counts only, never addresses). */
export interface AdminCampaignRow {
  kind: 'letter' | 'drop_letter' | 'restock_alert';
  /** Letter month (AAAA-MM), drop instant (ISO) or product slug. */
  ref: string;
  /** «La carta · noviembre de 2026» / «Carta del drop · 5 nov 19:00» / «Aviso de reposición · <producto>». */
  title: string;
  sentAt: string;
  sent: number;
  failed: number;
  skipped: number;
  /** Queued and not sent yet (letters: press again in Cartas / Hallazgos). */
  pending: number;
}

export type AdminOutboxView = 'pending' | 'sent' | 'archived' | 'all';

export interface AdminOutboxList {
  view: AdminOutboxView;
  /** Without `html` (the detail endpoint has it). */
  emails: Omit<AdminOutboxEmail, 'html'>[];
  /** Only in «Enviados» / «Todos» without a kind filter. */
  campaigns: AdminCampaignRow[];
}

/** One review request a prepare call created (or would create, dryRun). */
export interface AdminReviewPlanItem {
  title: string;
  to: string;
  locale: Locale;
  context: 'order' | 'service';
  orderNumber: string | null;
  /** The created draft (null in a simulation). */
  id: string | null;
}

export interface AdminReviewPrepareResult {
  dryRun: boolean;
  items: AdminReviewPlanItem[];
  skipped: { label: string; reason: string }[];
  /** Notes that do not block (e.g. «el email de envío aún no se ha mandado»). */
  warnings: string[];
}

export interface AdminOrderEmails {
  order: {
    orderNumber: string;
    customerName: string;
    email: string;
    /** Language the customer bought in (default language of the emails). */
    locale: Locale;
    paymentStatus: PaymentStatus;
    status: OrderStatus;
    shippingCountry: string;
  };
  emails: AdminOrderEmail[];
}

export interface AdminLogRow {
  createdAt: string;
  orderNumber: string;
  event: string;
  recipient: string | null;
  detail: string | null;
}

/**
 * The two newsletter subscriptions, each with its own double opt-in and
 * unsubscribe (docs/plan-newsletter-dos-listas.md, migration 0023):
 * `letter` = «La carta de CARO» (at most once a month), `finds` = «Hallazgos»
 * (only when there is a drop).
 */
export const NEWSLETTER_LISTS = ['letter', 'finds'] as const;
export type NewsletterList = (typeof NEWSLETTER_LISTS)[number];
/** A subscriber's state in one list: confirmed, asked but not confirmed, or opted out. */
export type NewsletterListState = 'active' | 'pending' | 'unsubscribed';

export interface AdminSubscriberRow {
  email: string;
  locale: string;
  consent: boolean;
  createdAt: string;
  /** ISO timestamp of the double opt-in confirmation, or null while pending. */
  confirmedAt?: string | null;
  /** ISO timestamp when the subscriber opted out, or null while active. */
  unsubscribedAt?: string | null;
  /** State in each list (0023); null = never asked for it. */
  lists?: Record<NewsletterList, NewsletterListState | null>;
}

/** One row of the admin Solicitudes view (an `enquiries` table record). */
export interface AdminEnquiryRow {
  id: string;
  /** 'contact' | 'product_enquiry' | 'manual_order' (see forms.ts / checkout.ts). */
  kind: string;
  name: string;
  email: string;
  productSlug: string | null;
  message: string;
  status: string;
  createdAt: string;
  /** Language of the form (null: sent before 0025 → treat as es). */
  locale: Locale | null;
  /** «Sesión realizada» (set in /admin → Solicitudes): enters the review requests. */
  servedAt: string | null;
}

// --- Admin (write) ---------------------------------------------------------

/**
 * One product row as sent by the /admin editor to the write API. Money is
 * integer cents (the browser converts euros → cents before POSTing, like
 * scripts/sync-catalog.mjs). The server validates this shape with Zod
 * (src/worker/lib/adminProducts.ts) before touching D1. It does NOT edit
 * vat/sku (left untouched on update, defaulted on create); metadata
 * is merged (see `mergeProductMetadata`). `id` is required on updates
 * (WHERE id=?), optional on creates (defaults to slug).
 */
export interface AdminProductWrite {
  id?: string;
  slug: string;
  name: string;
  brand: string | null;
  subtitle: string | null;
  caroNote: string | null;
  /**
   * Base (es) description → products.description. Omitted = left untouched (the
   * CSV import and the patch API never send it); '' / null clears it.
   */
  description?: string | null;
  category: ProductCategory;
  priceCents: number;
  /** The "compare-at" (struck-through) price = a discount when > priceCents. */
  compareAtPriceCents: number | null;
  currency: string;
  stockQuantity: number;
  inventoryTracking: boolean;
  status: ProductStatus;
  checkoutMode: CheckoutMode;
  featured: boolean;
  sortOrder: number;
  /** Relative media path, e.g. "/media/…" (no binary upload in this stage). */
  image: string | null;
  /**
   * Secondary images shown in the product page gallery after `image`: relative
   * media paths in display order → products.gallery. Omitted = gallery left
   * untouched. Alt texts of images already in the gallery are kept.
   */
  gallery?: string[];
  dataVerified: boolean;
  /** Reasortimiento (sells at 0 stock with a delay notice) → metadata.preorder. */
  preorder: boolean;
  /** One-off piece («Pieza vendida» once sold out, no restock alert) → metadata.uniquePiece. */
  uniquePiece: boolean;
  /**
   * Per-piece delivery overrides in integer cents (large pieces with their own
   * carrier cost) → metadata.delivery*Cents. null/0 = standard zone shipping.
   */
  deliverySpainCents: number | null;
  deliveryEuropeCents: number | null;
  deliveryInternationalCents: number | null;
  /**
   * Units per size for a sized product → metadata.sizeStock (REPLACES the map).
   * Omitted = untouched. Set only by the patch API (resolveSizeStockPatch), which
   * also sets `stockQuantity` to the sum.
   */
  sizeStock?: Record<string, number>;
  /**
   * Finds (metadata.findStory / findMeasurements / findCondition) and the
   * scheduled publication (metadata.publishAt, ISO with offset). Omitted =
   * untouched (the Catálogo grid, the CSV import and the patch API never send
   * them); null / '' removes the key.
   */
  findStory?: string | null;
  findMeasurements?: string | null;
  findCondition?: FindCondition | null;
  publishAt?: string | null;
  /**
   * Per-locale translations to MERGE into metadata.i18n (locales other than `es`;
   * `es` is the base). Only the fields present are written; empty removes them.
   */
  translations: Record<string, AdminProductTranslation> | null;
}

/** Admin-editable translation fields for a non-`es` locale (V1 subset). */
export interface AdminProductTranslation {
  name?: string | null;
  subtitle?: string | null;
  caroNote?: string | null;
  description?: string | null;
  findStory?: string | null;
  findMeasurements?: string | null;
}

/** Payload for POST /api/admin/products/batch — applied atomically (all or none). */
export interface AdminProductBatchInput {
  creates: AdminProductWrite[];
  updates: AdminProductWrite[];
}

/** Per-kind counts returned after a successful batch save. */
export interface AdminProductBatchResult {
  created: number;
  updated: number;
  /** Updates whose new status is "archived" (soft delete). Subset of `updated`. */
  archived: number;
}

// --- /admin Packs (docs/plan-packs.md, fase 6) ------------------------------

/** One component of a pack as /admin shows it (product fields null when it no longer exists). */
export interface AdminPackComponent {
  productId: string;
  quantity: number;
  slug: string | null;
  name: string | null;
  category: ProductCategory | null;
  priceCents: number | null;
  status: ProductStatus | null;
  image: string | null;
}

/** A pack in GET /api/admin/packs: every status, with computed price and availability. */
export interface AdminPackRow extends Pack {
  components: AdminPackComponent[];
  /** Σ price × quantity («antes»). */
  sumCents: number;
  /** Server-computed pack price (x,99). */
  priceCents: number;
  available: boolean;
  /** packAvailability reason codes (`pack_not_active`, `empty`, `<code>:<productId>`). */
  reasons: string[];
  /** Spanish default name («Pack 10% Belleza n1»), shown when `name` is null. */
  defaultName: string;
}

/** GET /api/admin/packs: the packs + the number the next pack of each store would get. */
export interface AdminPacksResponse {
  packs: AdminPackRow[];
  nextSeq: Record<ProductCategory, number>;
}

/** One component in a pack write: the product's SLUG (resolved server-side) and units. */
export interface AdminPackItemWrite {
  slug: string;
  quantity: number;
}

/**
 * Body of POST /api/admin/packs (create) and, every field optional, of
 * POST /api/admin/packs/:id (update). `i18n` merges per locale field ('' or
 * null removes it). `name`/`description` '' → null.
 */
export interface AdminPackWrite {
  slug?: string;
  name?: string | null;
  description?: string | null;
  discountPercent: number;
  status?: PackStatus;
  image?: string | null;
  sortOrder?: number;
  i18n?: Partial<Record<'en' | 'fr', { name?: string | null; description?: string | null }>>;
  items: AdminPackItemWrite[];
}

/**
 * One row of the /admin CSV catalog import (see src/lib/catalog/csv.ts). It is the
 * wire payload of POST /api/admin/products/import (validated server-side by
 * productImportSchema). Money is integer cents. `metadata` carries the FULL
 * ProductMetadata to REPLACE on import; `undefined` (a blank CSV cell) means "keep
 * the product's existing metadata". `id` empty/undefined = create; present = update.
 * `inventoryTracking` is not carried: import forces it true (parity with the editor).
 */
export interface CatalogImportRow {
  id?: string;
  slug: string;
  name: string;
  brand: string | null;
  subtitle: string | null;
  caroNote: string | null;
  category: ProductCategory;
  priceCents: number;
  compareAtPriceCents: number | null;
  currency: string;
  stockQuantity: number;
  status: ProductStatus;
  checkoutMode: CheckoutMode;
  featured: boolean;
  sortOrder: number;
  image: string | null;
  dataVerified: boolean;
  metadata?: ProductMetadata;
}

// --- API envelopes ---------------------------------------------------------

export interface ApiError {
  error: string;
  code: string;
  details?: unknown;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiError };
